/**
 * Voice endpointing config — the single place the hands-free loop's
 * end-of-utterance timing lives (design §7.1). Centralised on purpose so the
 * timings can be tuned from ONE spot after on-device measurement, not chased
 * through hooks and components.
 *
 * These apply only where the STT engine does NOT own endpointing itself — i.e.
 * native (the system recogniser keeps the segmented session open, so WE decide
 * when a turn ends). The web engine runs its own energy VAD in a worker and
 * emits `onEnd` directly, so it ignores these (see `STT_OWNS_ENDPOINTING`).
 *
 * The values are approximate starting points and UNVERIFIED on hardware — treat
 * them as the dials to turn once the false-endpoint metric is in place.
 */
import { Platform } from "react-native";

export interface EndpointConfig {
  /**
   * Trailing acoustic silence (ms) after the last real audio / transcript
   * change before a turn is considered done. This is the "natural pause" window
   * — long enough that an ordinary thinking pause doesn't cut a slow talker off.
   */
  quietMs: number;
  /** The transcript must also be unchanged for this long before ending. */
  transcriptStableMs: number;
  /**
   * Safety cap: if the transcript hasn't changed for this long, end the turn
   * even if the mic still reads as "voiced" — bounds a stuck-open mic in a room
   * whose steady noise sits above the adaptive floor (e.g. a loud café).
   */
  maxSilenceMs: number;
  /** Normalised level (0..1) above the adaptive noise floor that counts as voice. */
  voiceMargin: number;
  /** Absolute floor: never treat anything quieter than this as voice. */
  absVoice: number;
}

const CONFIG: Record<string, EndpointConfig> = {
  ios: {
    quietMs: 1400,
    transcriptStableMs: 400,
    maxSilenceMs: 6000,
    voiceMargin: 0.05,
    absVoice: 0.05,
  },
  android: {
    quietMs: 1600,
    transcriptStableMs: 500,
    maxSilenceMs: 6000,
    voiceMargin: 0.05,
    absVoice: 0.05,
  },
  default: {
    quietMs: 1500,
    transcriptStableMs: 450,
    maxSilenceMs: 6000,
    voiceMargin: 0.05,
    absVoice: 0.05,
  },
};

/** The endpoint config for the current platform. */
export function getEndpointConfig(): EndpointConfig {
  return CONFIG[Platform.OS] ?? CONFIG.default;
}
